(function () {
  const lists = document.querySelectorAll('[data-rewards-list]');
  if (!lists.length) return;

  // Find the URL the app's API lives at. Checks a few overrides (for dev),
  // then falls back to /apps/teacher in production. Delegates to the shared
  // resolver (window.RewardsBalance, set up in rewards-live-balance.js) so
  // the ?rewards_proxy= override applies consistently to the hero/sticky
  // bar too, not just here.
  function resolveProxyBase() {
    const first = lists[0];
    const sectionFallback = first && first.dataset.proxyBase;
    return window.RewardsBalance.resolveProxyBase(sectionFallback);
  }

  const proxyBase = resolveProxyBase();

  // Subscription selling plans handled by the internal subscription app need the
  // `_subscription_app` cart attribute, same as the self-bought plan sections do.
  async function markSubscriptionApp(sellingPlanId) {
    const plans = window.SUBSCRIPTION_APP_SELLING_PLAN_IDS || [];
    if (!plans.includes(Number(sellingPlanId))) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { _subscription_app: 'flaviar_internal' } }),
      });
    } catch (e) {
      console.error('[rewards] failed to set _subscription_app attribute', e);
    }
  }

  // Subscription option_id → { variantId, sellingPlan?, subscriptionPage?, isSubscription }
  // Rendered inline by sections/rewards-spend-points.liquid from section settings.
  const subscriptionProducts = (function () {
    const el = document.querySelector('script[data-rewards-subscription-products]');
    if (!el) return {};
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      const out = {};
      Object.keys(parsed).forEach((k) => {
        const v = parsed[k];
        if (v && v.variantId) out[String(k)] = v;
      });
      return out;
    } catch (e) {
      console.error('[rewards] failed to parse subscription products mapping', e);
      return {};
    }
  })();

  // Ordered product handles for the "Worth saving up for" section: pulled from
  // the main carousel and rendered in this order. Configured via theme editor.
  const worthSavingHandles = (function () {
    const el = document.querySelector('script[data-rewards-worth-saving-products]');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed.map((s) => String(s).trim()).filter(Boolean) : [];
    } catch (e) {
      console.error('[rewards] failed to parse worth-saving products list', e);
      return [];
    }
  })();
  const worthSavingHandleSet = new Set(worthSavingHandles);

  // Ordered product handles for the "Redeem for products" carousel: pinned to
  // the front in the configured order, remaining products follow in fetch order.
  function parseHandleList(selector) {
    const el = document.querySelector(selector);
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed.map((s) => String(s).trim()).filter(Boolean) : [];
    } catch (e) {
      console.error('[rewards] failed to parse handle list', selector, e);
      return [];
    }
  }

  const carouselSortHandles = parseHandleList('script[data-rewards-carousel-sort]');
  const carouselSortHandlesLast = parseHandleList('script[data-rewards-carousel-sort-last]');

  function bucketSubscriptions(items) {
    const isSubscriptionLike = (i) => {
      const name = (i.name || '').toLowerCase();
      const type = (i.type || '').toLowerCase();
      return /subscription|academy|founders|champions|founder/.test(name)
          || type === 'subscription';
    };
    const isDiscountLike = (i) => {
      const name = (i.name || '').toLowerCase();
      return /off|free shipping/.test(name);
    };
    return items.filter((i) => isSubscriptionLike(i) && !isDiscountLike(i));
  }

  // The /rewards/products endpoint returns enriched items with `title` and CDN
  // `image_url`. Normalize to the shape renderCard expects (uses `name`).
  function normalizeProduct(p) {
    return {
      id: p.id,
      cost_in_points: p.cost_in_points,
      name: p.title || p.yotpo_name || '',
      image_url: p.image_url,
      variant_id: p.variant_id,
      product_handle: p.product_handle,
      price: p.price,
      compare_at_price: p.compare_at_price,
      available: p.available,
    };
  }

  function formatNumber(n) {
    return (Number(n) || 0).toLocaleString();
  }

  // Hero "or a {bottle} on us" line — priciest product (by retail) the balance
  // can afford. Stays hidden when nothing is affordable.
  function updateWalletBottle(products, balance) {
    const line = document.querySelector('[data-rewards-wallet-bottle]');
    if (!line) return;
    const affordable = products
      .filter((p) => p.available !== false && balance >= (parseInt(p.cost_in_points, 10) || 0))
      .map((p) => ({ name: p.name, value: parseFloat(p.compare_at_price || p.price) }))
      .filter((p) => !isNaN(p.value));
    if (!affordable.length) {
      line.classList.add('d-none');
      return;
    }
    const best = affordable.reduce((a, b) => (b.value > a.value ? b : a));
    const nameEl = line.querySelector('[data-rewards-wallet-bottle-name]');
    if (nameEl) nameEl.textContent = best.name;
    line.classList.remove('d-none');
  }

  function formatRetailValue(item) {
    if (item.discount_with_currency) return item.discount_with_currency;
    const raw = item.compare_at_price || item.price;
    if (raw == null) return null;
    const num = parseFloat(raw);
    if (isNaN(num)) return null;
    return '$' + (num % 1 === 0 ? num.toFixed(0) : num.toFixed(2));
  }

  function renderCard(item, balance, anonymous) {
    const article = document.createElement('article');
    article.className = 'rewards-spend-points-card swiper-slide';
    if (item.id != null) article.dataset.itemId = String(item.id);

    const media = document.createElement('div');
    media.className = 'rewards-spend-points-card__media';
    if (item.image_url && /^(https?:)?\/\//.test(item.image_url)) {
      const img = document.createElement('img');
      img.src = item.image_url;
      img.alt = item.name || '';
      media.appendChild(img);
    }
    article.appendChild(media);

    const body = document.createElement('div');
    body.className = 'rewards-spend-points-card__body';

    const title = document.createElement('h4');
    title.className = 'rewards-spend-points-card__title';
    title.textContent = item.name || '';
    if (item.name) title.title = item.name;
    body.appendChild(title);

    const retail = formatRetailValue(item);
    if (retail) {
      const retailEl = document.createElement('p');
      retailEl.className = 'rewards-spend-points-card__retail';
      retailEl.innerHTML = 'Retail value: <strong>' + retail + '</strong>';
      body.appendChild(retailEl);
    }

    const redeem = document.createElement('div');
    redeem.className = 'rewards-spend-points-card__redeem';
    redeem.innerHTML =
      '<p class="rewards-spend-points-card__redeem-label">Redeem for:</p>' +
      '<p class="rewards-spend-points-card__points">' +
        '<span class="amount">' + formatNumber(item.cost_in_points) + '</span>' +
        '<span class="label">Points</span>' +
      '</p>';
    body.appendChild(redeem);
    article.appendChild(body);

    const cost = Number(item.cost_in_points) || 0;
    const locked = balance < cost;

    // Yotpo reports this option as currently unavailable (e.g. sold out).
    // Must be checked before the anonymous/locked branches below —
    // otherwise a sold-out option with enough points renders a live
    // "Redeem for points" button that fails server-side with the generic
    // redeem-error popup instead of never being clickable.
    if (item.available === false) {
      article.classList.add('is-unavailable');
      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'rewards-spend-points-card__cta rewards-btn';
      cta.textContent = 'Unavailable';
      cta.disabled = true;
      article.appendChild(cta);
      return article;
    }

    // Anonymous users have no balance; the card is purely promotional, so
    // suppress the "X points away" / progress / "Earn more points now" UI.
    if (anonymous) {
      return article;
    }

    if (locked) {
      article.classList.add('is-locked');
      const missing = cost - balance;
      const progress = cost > 0 ? Math.max(0, Math.min(100, Math.round((balance / cost) * 100))) : 0;
      const progressEl = document.createElement('div');
      progressEl.className = 'rewards-spend-points-card__progress';
      progressEl.innerHTML =
        '<p class="rewards-spend-points-card__progress-label">You&rsquo;re ' + formatNumber(missing) + ' points away</p>' +
        '<div class="rewards-spend-points-card__progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + progress + '">' +
          '<span style="width: ' + progress + '%"></span>' +
        '</div>';
      article.appendChild(progressEl);

      const earn = document.createElement('a');
      earn.className = 'rewards-spend-points-card__earn';
      earn.href = '#rewards-buy-points-intro';
      earn.textContent = 'Earn more points now';
      earn.addEventListener('click', (e) => {
        const target = document.getElementById('rewards-buy-points-intro')
          || document.querySelector('.rewards-buy-points-intro')
          || document.querySelector('.rewards-buy-points');
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      article.appendChild(earn);
    } else {
      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'rewards-spend-points-card__cta rewards-btn';
      cta.textContent = 'Redeem for points';
      if (item.id != null) cta.dataset.optionId = String(item.id);
      cta.addEventListener('click', () => redeemItem(item, cta));
      article.appendChild(cta);
    }

    return article;
  }

  // Copy per failure reason. Anything unmapped keeps the generic wording that
  // ships in snippets/rewards-redeem-error-popup.liquid.
  const REDEEM_ERROR_COPY = {
    sold_out: {
      title: 'This reward just sold out',
      copy: 'We couldn\u2019t add it to your cart. Pick another reward, or check back a little later.',
    },
    insufficient_points: {
      title: 'Not enough points yet',
      copy: 'You don\u2019t have enough points for this reward. Keep earning to unlock it.',
    },
    cart_add: {
      title: 'We couldn\u2019t add your reward',
      copy: 'Something went wrong adding this reward to your cart. Please try again in a few minutes.',
    },
    unavailable: {
      title: 'Rewards are unavailable right now',
      copy: 'We couldn\u2019t reach the rewards service. Please try again in a few minutes.',
    },
  };

  // The modal is reused across attempts, so the markup's own wording is kept as
  // the default and restored whenever a reason has no specific copy.
  let redeemErrorDefaults = null;

  // Show the redemption failure modal; fall back to an alert if it's unavailable.
  function showRedeemError(reason) {
    const el = document.getElementById('rewardsRedeemErrorPopup');
    const title = el && el.querySelector('.rewards-redeem-error-popup__title');
    const copy = el && el.querySelector('.rewards-redeem-error-popup__copy');
    if (el && !redeemErrorDefaults) {
      redeemErrorDefaults = {
        title: title ? title.textContent : 'We couldn\u2019t complete your redemption',
        copy: copy ? copy.textContent : 'Please try again in a few minutes.',
      };
    }
    const wording = REDEEM_ERROR_COPY[reason] || redeemErrorDefaults;
    if (wording) {
      if (title) title.textContent = wording.title;
      if (copy) copy.textContent = wording.copy;
    }
    if (el && window.bootstrap && window.bootstrap.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).show();
      return;
    }
    const fallback = REDEEM_ERROR_COPY[reason];
    window.alert(fallback
      ? fallback.title + ' ' + fallback.copy
      : 'We couldn\u2019t complete your redemption. Please try again in a few minutes.');
  }

  // Failures after Yotpo has created the redemption never reach the app: the
  // cart call goes browser-to-Shopify. Report them so they reach Slack.
  function reportRedeemFailure(payload) {
    try {
      fetch(proxyBase + '/rewards/redeem-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (e) {
      /* reporting must never break the redemption UI */
    }
  }

  // Tag an error so the catch block knows what to show and whether to report.
  function redeemError(message, opts) {
    const err = new Error(message);
    Object.assign(err, opts || {});
    return err;
  }

  // Fetch the cart drawer markup straight from the store root. Used when
  // cart/add.js comes back without usable sections.
  async function fetchCartSections(ids) {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(root + '?sections=' + ids.join(','), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return ids.every((id) => data && data[id]) ? data : null;
    } catch (e) {
      return null;
    }
  }

  // The section ids live on the drawer element, but redemption must work without it.
  function cartSectionIds(cartDrawer) {
    return cartDrawer
      ? cartDrawer.getSectionsToRender().map((s) => s.id)
      : ['cart-drawer', 'cart-icon-bubble'];
  }

  // Matches the drawer's own checkout link, so a locale-prefixed store keeps its prefix.
  function goToCheckout() {
    window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/checkout';
  }

  // The Section Rendering API returns `sections: null` when it can't render the
  // sections_url page; the redemption is already done, so never fail over it.
  async function showCartDrawer(cartDrawer, cartData) {
    if (!cartDrawer) {
      goToCheckout();
      return false;
    }
    const ids = cartSectionIds(cartDrawer);
    let sections = cartData && cartData.sections;
    if (!sections || ids.some((id) => !sections[id])) {
      sections = await fetchCartSections(ids);
    }
    if (sections) {
      const state = Object.assign({}, cartData, { sections: sections });
      try {
        if (typeof window.renderCartDrawer === 'function') {
          window.renderCartDrawer(state);
        } else {
          cartDrawer.renderContents(state);
          cartDrawer.classList.remove('is-empty');
        }
        return true;
      } catch (e) {
        console.error('[rewards] cart drawer render failed', e);
      }
    }
    goToCheckout();
    return false;
  }

  async function redeemItem(item, btn) {
    if (btn.disabled) return;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Processing…';
    try {
      const res = await fetch(proxyBase + '/rewards/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          option_id: item.id,
          type: item.variant_id ? 'product' : 'subscription',
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        // The app Slacks its own failures (insufficient points as a warning,
        // everything else via handleError), so these stay silent here.
        throw redeemError('redeem ' + res.status + ': ' + body, {
          reason: res.status === 422 && body.indexOf('enough points') !== -1
            ? 'insufficient_points'
            : 'unavailable',
          silent: true,
        });
      }
      const data = await res.json();

      // Product redemption: add to cart with Yotpo loyalty line item properties
      if (item.variant_id) {
        const redemptionId = data && data.redemption_id;
        const redemptionToken = data && data.redemption_token;
        if (!redemptionId || !redemptionToken) {
          throw redeemError('missing redemption id/token', {
            reason: 'unavailable',
            stage: 'missing_token',
          });
        }

        const cartDrawer = document.querySelector('cart-drawer');

        const cartRes = await fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{
              id: Number(item.variant_id),
              quantity: 1,
              properties: {
                _swell_points_used: String(item.cost_in_points),
                _yotpo_loyalty_redemption_id: String(redemptionId),
                _yotpo_loyalty_redemption_token: String(redemptionToken),
                _yotpo_loyalty_discount_type: 'product',
              },
            }],
            sections: cartSectionIds(cartDrawer),
            sections_url: window.location.pathname,
          }),
        });
        if (!cartRes.ok) {
          const body = await cartRes.text();
          throw redeemError('cart/add ' + cartRes.status + ': ' + body, {
            reason: body.toLowerCase().indexOf('sold out') !== -1 ? 'sold_out' : 'cart_add',
            stage: 'cart_add',
            status: cartRes.status,
            detail: body,
            redemptionId: redemptionId,
          });
        }
        const cartData = await cartRes.json();
        const shown = await showCartDrawer(cartDrawer, cartData);
        if (!shown) {
          reportRedeemFailure({
            option_id: item.id,
            stage: 'cart_render',
            status: null,
            message: 'cart sections unavailable, sent the customer to checkout',
            redemption_id: redemptionId,
          });
          return;
        }
        btn.disabled = false;
        btn.textContent = originalText;
        return;
      }

      const couponCode = data && data.coupon_code;
      if (!couponCode) throw new Error('no coupon');

      // Subscription / academy redemption (option_id mapped to a Shopify variant + selling plan)
      const sub = subscriptionProducts[String(item.id)];
      if (sub && sub.variantId) {
        const cartDrawer = document.querySelector('cart-drawer');

        // Match widget behaviour — drop existing subscription/membership items first
        // so the new redemption isn't combined with a stale one.
        if (typeof window.removeProductTypeFromCart === 'function' && sub.isSubscription) {
          try {
            await window.removeProductTypeFromCart('Subscription', 'Membership');
          } catch (e) { /* non-fatal */ }
        }

        // Apply discount first so Shopify attaches it to the new line item
        // when cart/add.js runs (mirrors the widget response shape).
        if (typeof window.applyDiscount === 'function') {
          try { await window.applyDiscount(couponCode); } catch (e) { /* non-fatal */ }
        }

        const cartItem = { id: String(sub.variantId), quantity: 1 };
        if (sub.isSubscription && sub.sellingPlan) {
          cartItem.selling_plan = parseInt(sub.sellingPlan, 10);
          cartItem.properties = {
            '_ordergroove.subscription.prepaidOrdersPerBilling': 1,
            '_subscription_page': sub.subscriptionPage || '',
          };
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const cartRes = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [cartItem],
            sections: cartSectionIds(cartDrawer),
            sections_url: window.location.pathname,
          }),
        });
        if (!cartRes.ok) {
          const body = await cartRes.text();
          throw redeemError('cart/add ' + cartRes.status + ': ' + body, {
            reason: body.toLowerCase().indexOf('sold out') !== -1 ? 'sold_out' : 'cart_add',
            stage: 'cart_add',
            status: cartRes.status,
            detail: body,
          });
        }

        if (cartItem.selling_plan) await markSubscriptionApp(cartItem.selling_plan);

        const cartData = await cartRes.json();
        const shown = await showCartDrawer(cartDrawer, cartData);
        if (!shown) {
          reportRedeemFailure({
            option_id: item.id,
            stage: 'cart_render',
            status: null,
            message: 'cart sections unavailable, sent the customer to checkout',
            redemption_id: null,
          });
          return;
        }
        btn.disabled = false;
        btn.textContent = originalText;
        return;
      }

      // Fallback: pure coupon / shipping redemption
      if (typeof window.applyDiscount === 'function') {
        await window.applyDiscount(couponCode);
        window.location.href = '/checkout';
        return;
      }
      window.location.href = '/discount/' + encodeURIComponent(couponCode);
    } catch (err) {
      console.error('[rewards] redeem failed', err);
      if (!err || !err.silent) {
        reportRedeemFailure({
          option_id: item.id,
          stage: (err && err.stage) || 'unknown',
          status: (err && err.status) || null,
          message: (err && (err.detail || err.message)) || 'unknown error',
          redemption_id: (err && err.redemptionId) || null,
        });
      }
      btn.disabled = false;
      btn.textContent = originalText;
      showRedeemError(err && err.reason);
    }
  }

  async function fetchJson(path) {
    const res = await fetch(proxyBase + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(path + ' ' + res.status);
    const data = await res.json();
    return Array.isArray(data && data.items) ? data.items : [];
  }

  // The metafield balance rendered into data-balance is synced from Yotpo
  // asynchronously and can lag behind the live figure. When Yotpo's own
  // balance is reachable, prefer it, less the points held by rewards already
  // in the cart (Yotpo only deducts those at checkout) — for this session
  // only, never written back to the metafield. Shares its fetches
  // (window.RewardsBalance, set up in rewards-live-balance.js) with the
  // hero/sticky bar instead of firing its own independent requests.
  const anyLoggedIn = Array.from(lists).some((l) => l.dataset.anonymous !== 'true');
  const metafieldBalance = parseInt((lists[0] && lists[0].dataset.balance) || '0', 10) || 0;

  function fetchBalance() {
    if (!anyLoggedIn) return Promise.resolve(null);
    return window.RewardsBalance.fetchEffectiveBalance(proxyBase, metafieldBalance);
  }

  // True if any item in the bucket flips locked/unlocked between the two
  // balances — i.e. a full re-render would actually look different.
  function lockStateChanged(bucket, balanceBefore, balanceAfter) {
    return bucket.some((item) => {
      const cost = Number(item.cost_in_points) || 0;
      return (balanceBefore < cost) !== (balanceAfter < cost);
    });
  }

  // Cheaper alternative to renderWithBalance for when the balance changed
  // but nothing crossed a lock/unlock threshold: update the "X points away"
  // text/bar on cards that remain locked, without touching the DOM structure
  // or the Swiper instance.
  function refreshLockedCardProgress(list, bucket, balance) {
    const container = list.querySelector('.rewards-spend-points-card-list__items') || list;
    bucket.forEach((item) => {
      const cost = Number(item.cost_in_points) || 0;
      if (balance >= cost || item.id == null) return;
      const card = container.querySelector('[data-item-id="' + item.id + '"]');
      if (!card) return;
      const missing = cost - balance;
      const progress = cost > 0 ? Math.max(0, Math.min(100, Math.round((balance / cost) * 100))) : 0;
      const label = card.querySelector('.rewards-spend-points-card__progress-label');
      if (label) label.innerHTML = 'You&rsquo;re ' + formatNumber(missing) + ' points away';
      const bar = card.querySelector('.rewards-spend-points-card__progress-bar');
      if (bar) {
        bar.setAttribute('aria-valuenow', String(progress));
        const fill = bar.querySelector('span');
        if (fill) fill.style.width = progress + '%';
      }
    });
  }

  // Renders/re-renders all lists against whatever balance currently sits in
  // each list's dataset.balance. Called once with the metafield-rendered
  // balance, then again if the live balance turns out to differ.
  function renderWithBalance(products, groups) {
    const walletBalance = parseInt((lists[0] && lists[0].dataset.balance) || '0', 10) || 0;
    updateWalletBottle(products, walletBalance);

    lists.forEach((list) => {
      const groupIndex = list.dataset.group;
      const balance = parseInt(list.dataset.balance, 10) || 0;
      const anonymous = list.dataset.anonymous === 'true';
      const bucket = groups[groupIndex] || [];
      const target = list.querySelector('.rewards-spend-points-card-list__items') || list;
      target.innerHTML = '';
      bucket.forEach((item) => target.appendChild(renderCard(item, balance, anonymous)));
      list.classList.toggle('is-empty', bucket.length === 0);
      initSwiper(list);
      const carousel = list.closest('.rewards-spend-points-carousel');
      if (carousel) carousel.classList.remove('is-loading');
    });
  }

  async function init() {
    // Kicked off immediately, in parallel with the products/redemptions
    // fetch below — it has no data dependency on them, so there's no reason
    // to wait for those (and the first metafield-based render) before
    // starting it.
    const liveBalancePromise = fetchBalance();

    let enriched = [];
    let redemptions = [];
    const results = await Promise.allSettled([
      fetchJson('/rewards/products'),
      fetchJson('/rewards/redemptions'),
    ]);
    if (results[0].status === 'fulfilled') {
      enriched = results[0].value.map(normalizeProduct);
    } else {
      console.error('[rewards] failed to load products', results[0].reason);
    }
    if (results[1].status === 'fulfilled') {
      redemptions = results[1].value;
    } else {
      console.error('[rewards] failed to load redemptions', results[1].reason);
    }
    if (!enriched.length && !redemptions.length) {
      lists.forEach((l) => {
        const carousel = l.closest('.rewards-spend-points-carousel');
        if (carousel) carousel.classList.remove('is-loading');
        l.classList.add('has-error');
        const items = l.querySelector('.rewards-spend-points-card-list__items') || l;
        items.innerHTML = '';
      });
      return;
    }

    // Split enriched (variant-backed) items by the subscription mapping.
    // Anything in subscriptionProducts → subscription carousel; rest → products.
    const products = [];
    const enrichedSubs = [];
    enriched.forEach((item) => {
      if (subscriptionProducts[String(item.id)]) enrichedSubs.push(item);
      else products.push(item);
    });

    // Pull worth-saving items out of the products bucket, preserving the
    // configured order regardless of what order Yotpo returned them in.
    const worthSavingByHandle = {};
    const remainingProducts = [];
    products.forEach((item) => {
      if (item.product_handle && worthSavingHandleSet.has(item.product_handle)) {
        worthSavingByHandle[item.product_handle] = item;
      } else {
        remainingProducts.push(item);
      }
    });
    const worthSaving = worthSavingHandles
      .map((h) => worthSavingByHandle[h])
      .filter(Boolean);

    // Coupon-style subscriptions (Champions, Founders, Academy) lack a variant_id
    // so aren't enriched; pull image + title from the subscriptionProducts mapping.
    const couponSubs = bucketSubscriptions(redemptions)
      .filter((item) => subscriptionProducts[String(item.id)])
      .map((item) => {
        const m = subscriptionProducts[String(item.id)];
        // Override image/name only — leaving variant_id absent keeps redeemItem
        // on the coupon/subscription branch, not the product-redemption one.
        return {
          ...item,
          image_url: m.imageUrl || item.image_url,
          name: m.title || item.name,
        };
      });

    const subscriptions = enrichedSubs.length
      ? enrichedSubs
      : couponSubs;

    let sortedProducts = remainingProducts;
    if (carouselSortHandles.length || carouselSortHandlesLast.length) {
      const firstSet = new Set(carouselSortHandles);
      const lastSet = new Set(carouselSortHandlesLast);
      const firstByHandle = {};
      const lastByHandle = {};
      const unpinned = [];
      remainingProducts.forEach((item) => {
        const h = item.product_handle;
        if (h && firstSet.has(h)) firstByHandle[h] = item;
        else if (h && lastSet.has(h)) lastByHandle[h] = item;
        else unpinned.push(item);
      });
      const pinned = carouselSortHandles.map((h) => firstByHandle[h]).filter(Boolean);
      const pinnedLast = carouselSortHandlesLast.map((h) => lastByHandle[h]).filter(Boolean);
      sortedProducts = pinned.concat(unpinned, pinnedLast);
    }

    // Sold-out/unavailable options shouldn't clutter the front of the
    // carousel — push them after everything redeemable, regardless of any
    // pin configured above. Array#sort is a stable sort, so relative order
    // within "available" and within "unavailable" is otherwise unchanged.
    sortedProducts = sortedProducts
      .slice()
      .sort((a, b) => (a.available === false ? 1 : 0) - (b.available === false ? 1 : 0));

    const groups = {
      1: sortedProducts,
      2: subscriptions,
      'worth-saving': worthSaving,
    };

    // First paint: the metafield balance Liquid already put in dataset.balance.
    renderWithBalance(products, groups);

    // Then check the live balance; if it differs, swap it in and recalculate
    // afford-locks, the wallet bottle, and re-render the cards. Repeated
    // whenever the cart changes, so a reward added or removed moves the
    // balance straight away.
    applyBalance(products, groups, await liveBalancePromise);
    if (anyLoggedIn) {
      window.RewardsBalance.onBalanceChange(() => {
        fetchBalance().then((balance) => applyBalance(products, groups, balance));
      });
    }
  }

  function applyBalance(products, groups, liveBalance) {
    if (liveBalance == null) return;
    const currentBalance = parseInt((lists[0] && lists[0].dataset.balance) || '0', 10) || 0;
    if (liveBalance === currentBalance) return;
    lists.forEach((l) => { l.dataset.balance = String(liveBalance); });

    // Skip the full rebuild (which tears down and reinitializes Swiper) when
    // the correction doesn't actually change what's on screen — i.e. no
    // card in any list crosses its lock/unlock threshold. Just refresh the
    // wallet bottle and the "X points away" progress in place for cards
    // that stay locked.
    const anyThresholdCrossed = Object.keys(groups).some((groupIndex) =>
      lockStateChanged(groups[groupIndex] || [], currentBalance, liveBalance)
    );

    if (!anyThresholdCrossed) {
      updateWalletBottle(products, liveBalance);
      lists.forEach((list) => {
        const bucket = groups[list.dataset.group] || [];
        refreshLockedCardProgress(list, bucket, liveBalance);
      });
      return;
    }

    renderWithBalance(products, groups);
  }

  function initSwiper(list) {
    if (typeof window.Swiper !== 'function') return;
    // renderWithBalance can run a second time once the live balance resolves;
    // tear down the previous instance before re-initializing on fresh cards.
    if (list.swiper && typeof list.swiper.destroy === 'function') {
      list.swiper.destroy(true, true);
    }
    const carousel = list.closest('.rewards-spend-points-carousel');
    const group = list.closest('.rewards-spend-points-group');
    const prevEl = carousel && carousel.querySelector('.rewards-spend-points-arrow--prev');
    const nextEl = carousel && carousel.querySelector('.rewards-spend-points-arrow--next');
    const paginationEl = group && group.querySelector('.swiper-pagination');
    const options = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      watchOverflow: true,
      centerInsufficientSlides: true,
      breakpoints: {
        992: { spaceBetween: 24, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
      },
    };
    if (prevEl && nextEl) options.navigation = { prevEl: prevEl, nextEl: nextEl };
    if (paginationEl) options.pagination = { el: paginationEl, clickable: true };
    const swiper = new window.Swiper(list, options);
    const refresh = () => {
      if (!swiper || swiper.destroyed) return;
      swiper.update();
      if (swiper.navigation && swiper.navigation.update) swiper.navigation.update();
    };
    requestAnimationFrame(refresh);
    list.querySelectorAll('img').forEach((img) => {
      img.addEventListener('load', refresh, { once: true });
      img.addEventListener('error', refresh, { once: true });
    });
    window.addEventListener('load', refresh, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
